import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { DEFAULT_HOME_ROUTE } from '../../../config/navAccessMap';
import { useMultiOrgLogin } from '@saas-foundation/shared-frontend/auth/useMultiOrgLogin';
import { OrganizationSelectFields } from '@saas-foundation/shared-frontend/auth/OrganizationSelectFields';
import { EmailVerificationNotice } from '@saas-foundation/shared-frontend/auth/EmailVerificationNotice';
import PasswordInput from '@saas-foundation/shared-frontend/auth/PasswordInput';
import { getSaasApiBaseUrl } from '../../../utils/saasApiBaseUrl';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaStep, setMfaStep] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [emailVerification, setEmailVerification] = useState(null);

  const { login, completeLogin, mfaVerify, resendMfa } = useAuthContext();
  const navigate = useNavigate();

  const {
    inviteContext,
    inviteOrgHint,
    showOrgDropdown,
    orgSelection,
    selectedOrgId,
    setSelectedOrgId,
    loginSubtitle,
    resetOrganizationSelection,
    beginOrganizationSelection,
    runCredentialLogin,
    runOrganizationLogin,
  } = useMultiOrgLogin({
    login,
    completeLogin,
    getApiBaseUrl: getSaasApiBaseUrl,
  });

  const navigateAfterLogin = () => {
    navigate(inviteContext.redirectPath || DEFAULT_HOME_ROUTE);
  };

  const showMfaStep = mfaStep?.mfaToken;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEmailVerification(null);

    try {
      if (showOrgDropdown) {
        await runOrganizationLogin();
        navigateAfterLogin();
        return;
      }

      if (showMfaStep && mfaStep?.mfaToken && mfaCode.trim().length >= 6) {
        const result = await mfaVerify(mfaStep.mfaToken, mfaCode.trim());
        if (result?.success && result.requiresOrganizationSelection) {
          beginOrganizationSelection(result);
          return;
        }
        if (result?.success) {
          navigateAfterLogin();
        }
        return;
      }

      if (showMfaStep) {
        setError('Please enter the 6-digit code.');
        return;
      }

      const { handled, result, needsOrgSelection } = await runCredentialLogin(formData);
      if (result?.requiresEmailVerification) {
        setEmailVerification({
          email: result.email || formData.email,
          message: result.message,
        });
        return;
      }
      if (handled) {
        if (needsOrgSelection) {
          return;
        }
        if (result?.success && result.requiresMfa) {
          setMfaStep({ mfaToken: result.mfaToken, mfaMethod: result.mfaMethod, message: result.message });
          setMfaCode('');
          return;
        }
        if (result?.success) {
          navigateAfterLogin();
          return;
        }
        setError(result?.error || 'Login failed');
        return;
      }

      if (result?.success && result.requiresMfa) {
        setMfaStep({ mfaToken: result.mfaToken, mfaMethod: result.mfaMethod, message: result.message });
        setMfaCode('');
        return;
      }

      if (result?.success) {
        navigateAfterLogin();
        return;
      }

      setError(result?.error || 'Login failed');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    resetOrganizationSelection();
    setMfaStep(null);
    setMfaCode('');
    setError('');
  };

  const handleResendMfa = async () => {
    if (!mfaStep?.mfaToken) return;
    setError('');
    try {
      const result = await resendMfa(mfaStep.mfaToken);
      if (result?.mfaToken) {
        setMfaStep((prev) => ({ ...prev, mfaToken: result.mfaToken, message: 'Code resent.' }));
      }
    } catch (err) {
      setError(err.message || 'Could not resend code.');
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AgenticPractice</h1>
          <p className="text-gray-600 mt-2">AI-Native all-in-one RCM Platform</p>
          <h2 className="text-2xl font-semibold text-gray-800 mt-6">
            {showOrgDropdown ? 'Choose organization' : 'Sign In'}
          </h2>
          <p className="text-gray-600 mt-2">
            {showMfaStep
              ? (mfaStep?.mfaMethod === 'totp'
                ? 'Enter the 6-digit code from your authenticator app'
                : 'Enter the 6-digit verification code')
              : loginSubtitle}
          </p>
        </div>

        {emailVerification && (
          <EmailVerificationNotice
            email={emailVerification.email}
            message={emailVerification.message}
            getApiBaseUrl={getSaasApiBaseUrl}
            sourceApp="agentic_rcm"
          />
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {showMfaStep ? (
            <>
              <div>
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification code
                </label>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-widest"
                  placeholder="000000"
                />
                {mfaStep.message && (
                  <p className="text-sm text-gray-500 mt-1">{mfaStep.message}</p>
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                {mfaStep?.mfaMethod !== 'totp' ? (
                  <button type="button" onClick={handleResendMfa} className="text-blue-600 hover:text-blue-500">
                    Resend code
                  </button>
                ) : (
                  <span className="text-gray-500">Open your authenticator app for a new code</span>
                )}
                <button type="button" onClick={handleBackToCredentials} className="text-gray-600 hover:text-gray-800">
                  Back to sign in
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  readOnly={showOrgDropdown}
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${showOrgDropdown ? 'bg-gray-50 text-gray-600' : ''}`}
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <PasswordInput
                  id="password"
                  name="password"
                  required
                  readOnly={showOrgDropdown}
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${showOrgDropdown ? 'bg-gray-50 text-gray-600' : ''}`}
                  placeholder="••••••••"
                />
                {!showOrgDropdown && (
                  <div className="mt-1 text-right">
                    <Link
                      to={`/forgot-password${formData.email ? `?email=${encodeURIComponent(formData.email)}` : ''}`}
                      className="text-sm text-blue-600 hover:text-blue-500"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}
              </div>

              {showOrgDropdown && (
                <OrganizationSelectFields
                  organizations={orgSelection.organizations}
                  selectedOrgId={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  onBack={handleBackToCredentials}
                  inviteOrgHint={inviteOrgHint}
                />
              )}
            </>
          )}

          <button
            type="submit"
            disabled={loading || (showMfaStep && mfaCode.trim().length < 6)}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : showMfaStep ? 'Verify' : showOrgDropdown ? 'Continue' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
